import React, { useMemo } from 'react';
import {
  ChevronDown,
  Pencil,
  Settings,
  Trash2,
  UserCheck,
  UserX,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../../../ui/avatar';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Card } from '../../../ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import {
  formatLabel,
  getDadRelationshipStage,
  getMemberTierLabel,
  MEMBER_TIER_COLORS,
  stageColors,
} from './memberConstants';

const sectionHeaderClass =
  'text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-[#16161a] font-medium px-4 py-2';

const MemberRow = ({
  member,
  showRemoved,
  selectedDadIds,
  toggleDadSelection,
  openEditModal,
  openRolesModal,
  openRemoveModal,
  handleReinstateMember,
  reinstatingId,
  openHardDeleteModal,
}) => {
  const relationshipStage = getDadRelationshipStage(member);
  const isFounding40 = (member.cohorts || []).includes('founding40');

  return (
    <tr
      key={member.user_id}
      className="border-b border-gray-200 dark:border-[#2a2a32] hover:bg-gray-100 dark:hover:bg-[#16161a]"
    >
      <td className="p-4 align-top">
        <input
          type="checkbox"
          checked={selectedDadIds.includes(member.user_id)}
          onChange={() => toggleDadSelection(member.user_id)}
          className="mt-1 h-4 w-4 rounded accent-aqua"
        />
      </td>
      <td className="p-4">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10">
            <AvatarImage src={member.picture} />
            <AvatarFallback className="bg-aqua/20 text-aqua">
              {member.name?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{member.name}</p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Account ID: <code className="select-all break-all font-mono text-[11px] text-gray-600 dark:text-gray-300">{member.user_id}</code>
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {relationshipStage ? (
                <Badge
                  className={`${stageColors[relationshipStage] || stageColors.prospect} border text-xs`}
                >
                  {formatLabel(relationshipStage)}
                </Badge>
              ) : null}
              {isFounding40 ? (
                <Badge className="border border-orange-500/25 bg-orange-500/15 text-xs text-orange-700 dark:text-orange-300">
                  Founding 40
                </Badge>
              ) : null}
              {showRemoved && member.status === 'former' ? (
                <Badge className="border border-red-500/25 bg-red-500/15 text-xs text-red-700 dark:text-red-300">
                  Former
                </Badge>
              ) : null}
              {showRemoved && member.status === 'trial' ? (
                <Badge className="border border-yellow-500/25 bg-yellow-500/15 text-xs text-yellow-700 dark:text-yellow-300">
                  Trial
                </Badge>
              ) : null}
            </div>
          </div>
        </div>
      </td>
      <td className="p-4 text-sm text-gray-600 dark:text-gray-400">
        <div>{member.email ?? '—'}</div>
        {member.source || member.warmth ? (
          <div className="mt-1 flex flex-wrap gap-2">
            {member.source ? (
              <span className="text-xs text-gray-500 dark:text-gray-500">
                {formatLabel(member.source)}
              </span>
            ) : null}
            {member.warmth ? (
              <span className="text-xs text-gray-500 dark:text-gray-500">
                {formatLabel(member.warmth)}
              </span>
            ) : null}
          </div>
        ) : null}
      </td>
      <td className="p-4 text-sm text-gray-600 dark:text-gray-400">{member.phone ?? '—'}</td>
      <td className="p-4">
        <div className="flex flex-wrap gap-1">
          <Badge className={MEMBER_TIER_COLORS[member.tier] || MEMBER_TIER_COLORS.member}>
            {getMemberTierLabel(member.tier || 'member')}
          </Badge>
          {member.additional_roles?.map((role) => (
            <Badge
              key={role}
              className={`${MEMBER_TIER_COLORS[role] || MEMBER_TIER_COLORS.member} opacity-75`}
            >
              +{getMemberTierLabel(role)}
            </Badge>
          ))}
        </div>
      </td>
      <td className="p-4 text-sm text-gray-600 dark:text-gray-400">
        <div>{member.biggest_pressure_area ? formatLabel(member.biggest_pressure_area) : '—'}</div>
        {member.children_age_range ? (
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-500">
            {formatLabel(member.children_age_range)}
          </div>
        ) : null}
      </td>
      <td className="p-4 text-sm text-gray-600 dark:text-gray-400">
        {member.created_at ? new Date(member.created_at).toLocaleDateString() : 'N/A'}
      </td>
      <td className="p-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              className="border-gray-300 text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:border-[#2a2a32] dark:text-gray-400 dark:hover:bg-[#252530] dark:hover:text-white"
            >
              Actions
              <ChevronDown className="ml-1 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="border-gray-200 bg-white dark:border-[#2a2a32] dark:bg-[#1e1e24]"
          >
            <DropdownMenuItem
              className="cursor-pointer text-gray-900 focus:bg-gray-100 focus:text-gray-900 dark:text-gray-300 dark:focus:bg-[#252530] dark:focus:text-white"
              onClick={() => openEditModal(member)}
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>

            <DropdownMenuItem
              className="cursor-pointer text-gray-900 focus:bg-gray-100 focus:text-gray-900 dark:text-gray-300 dark:focus:bg-[#252530] dark:focus:text-white"
              onClick={() => openRolesModal(member)}
            >
              <Settings className="mr-2 h-4 w-4" />
              Roles
            </DropdownMenuItem>

            <DropdownMenuItem
              className="cursor-pointer text-gray-900 focus:bg-gray-100 focus:text-gray-900 dark:text-gray-300 dark:focus:bg-[#252530] dark:focus:text-white"
              onClick={() => openRemoveModal(member)}
            >
              <UserX className="mr-2 h-4 w-4" />
              Remove…
            </DropdownMenuItem>

            {member.status === 'former' ? (
              <DropdownMenuItem
                className="cursor-pointer text-green-600 focus:bg-green-500/10 focus:text-green-600 dark:text-green-400 dark:focus:text-green-400"
                onClick={() => handleReinstateMember(member)}
                disabled={reinstatingId === member.user_id}
              >
                {reinstatingId === member.user_id ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <UserCheck className="mr-2 h-4 w-4" />
                )}
                Reinstate
              </DropdownMenuItem>
            ) : null}

            {member.tier !== 'admin' ? (
              <DropdownMenuItem
                className="cursor-pointer text-red-500 focus:bg-red-500/10 focus:text-red-500 dark:text-red-400 dark:focus:text-red-400"
                onClick={() => openHardDeleteModal(member)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Permanently delete…
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
};

const MembersDirectoryTable = ({
  filteredMembers,
  showRemoved,
  selectedDadIds,
  toggleDadSelection,
  openEditModal,
  openRolesModal,
  openRemoveModal,
  handleReinstateMember,
  reinstatingId,
  openHardDeleteModal,
}) => {
  const activeMembers = useMemo(
    () =>
      showRemoved
        ? filteredMembers.filter(
            (member) =>
              member.status == null || member.status === undefined || member.status === 'active',
          )
        : [],
    [filteredMembers, showRemoved],
  );
  const trialMembers = useMemo(
    () => (showRemoved ? filteredMembers.filter((member) => member.status === 'trial') : []),
    [filteredMembers, showRemoved],
  );
  const formerMembers = useMemo(
    () => (showRemoved ? filteredMembers.filter((member) => member.status === 'former') : []),
    [filteredMembers, showRemoved],
  );

  const rows = useMemo(() => {
    const buildMemberRow = (member) => (
      <MemberRow
        key={member.user_id}
        member={member}
        showRemoved={showRemoved}
        selectedDadIds={selectedDadIds}
        toggleDadSelection={toggleDadSelection}
        openEditModal={openEditModal}
        openRolesModal={openRolesModal}
        openRemoveModal={openRemoveModal}
        handleReinstateMember={handleReinstateMember}
        reinstatingId={reinstatingId}
        openHardDeleteModal={openHardDeleteModal}
      />
    );

    if (!showRemoved) {
      return filteredMembers.map(buildMemberRow);
    }

    return [
      <tr key="section-active">
        <td colSpan={8} className={sectionHeaderClass}>
          Active ({activeMembers.length})
        </td>
      </tr>,
      ...activeMembers.map(buildMemberRow),
      ...(trialMembers.length > 0
        ? [
            <tr key="section-trial">
              <td colSpan={8} className={sectionHeaderClass}>
                Trial ({trialMembers.length})
              </td>
            </tr>,
            ...trialMembers.map(buildMemberRow),
          ]
        : []),
      ...(formerMembers.length > 0
        ? [
            <tr key="section-former">
              <td colSpan={8} className={sectionHeaderClass}>
                Former ({formerMembers.length})
              </td>
            </tr>,
            ...formerMembers.map(buildMemberRow),
          ]
        : []),
    ];
  }, [
    activeMembers,
    filteredMembers,
    formerMembers,
    handleReinstateMember,
    openEditModal,
    openHardDeleteModal,
    openRemoveModal,
    openRolesModal,
    reinstatingId,
    selectedDadIds,
    showRemoved,
    toggleDadSelection,
    trialMembers,
  ]);

  return (
    <Card className="overflow-hidden border-[#2a2a32] bg-[#1e1e24]">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-[#2a2a32]">
              <th className="p-4 text-left font-medium text-gray-600 dark:text-gray-400">Select</th>
              <th className="p-4 text-left font-medium text-gray-600 dark:text-gray-400">Dad</th>
              <th className="p-4 text-left font-medium text-gray-600 dark:text-gray-400">Email</th>
              <th className="p-4 text-left font-medium text-gray-600 dark:text-gray-400">Phone</th>
              <th className="p-4 text-left font-medium text-gray-600 dark:text-gray-400">Tier</th>
              <th className="p-4 text-left font-medium text-gray-600 dark:text-gray-400">Pipeline</th>
              <th className="p-4 text-left font-medium text-gray-600 dark:text-gray-400">Joined</th>
              <th className="p-4 text-left font-medium text-gray-600 dark:text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMembers.length === 0 ? (
              <tr>
                <td colSpan={8} className="p-8 text-center text-gray-500">
                  No dads found
                </td>
              </tr>
            ) : (
              rows
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default MembersDirectoryTable;
